/**
 * UserManagement Component - Extracted from Settings.jsx
 * Manages user accounts: create, delete, password reset, view logs
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Users, Key, History, Circle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage, formatDateTime } from '@/lib/utils';
import axios from 'axios';
import { BACKEND_URL } from '@/lib/api';

const API = `${BACKEND_URL}/api`;

const ROLE_BADGES = {
  admin: { label: 'Admin', color: 'bg-red-100 text-red-800' },
  general_manager: { label: 'General Manager', color: 'bg-purple-100 text-purple-800' },
  accountant: { label: 'Accountant', color: 'bg-blue-100 text-blue-800' },
  cashier: { label: 'Cashier', color: 'bg-green-100 text-green-800' },
  storekeeper: { label: 'Storekeeper', color: 'bg-orange-100 text-orange-800' },
  bartender: { label: 'Bartender', color: 'bg-amber-100 text-amber-800' },
  kitchen: { label: 'Kitchen', color: 'bg-yellow-100 text-yellow-800' },
  cafe: { label: 'Cafe', color: 'bg-cyan-100 text-cyan-800' },
  team_member: { label: 'Team Member', color: 'bg-gray-100 text-gray-800' },
  partner: { label: 'Partner', color: 'bg-indigo-100 text-indigo-800' },
};

const UserManagement = ({ users, currentUserId, onRefresh }) => {
  const [userDialog, setUserDialog] = useState(false);
  const [deleteUserDialog, setDeleteUserDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [passwordDialog, setPasswordDialog] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState('');
  const [logsDialog, setLogsDialog] = useState({ open: false, user: null, sessions: [], activities: [] });
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    outlet: '',
    role: 'cashier'
  });

  const getRoleBadge = (role) => {
    const badge = ROLE_BADGES[role] || { label: role, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={badge.color}>{badge.label}</Badge>;
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (userForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setAddingUser(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users`, userForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User ${userForm.name} created`);
      setUserForm({ name: '', email: '', password: '', phone: '', outlet: '', role: 'cashier' });
      setUserDialog(false);
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setAddingUser(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeletingUser(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/${userToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      setDeleteUserDialog(false);
      setUserToDelete(null);
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeletingUser(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordDialog.user || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/${passwordDialog.user.id}/password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Password changed for ${passwordDialog.user.name}`);
      setPasswordDialog({ open: false, user: null });
      setNewPassword('');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setChangingPassword(false);
    }
  };

  const handleViewLogs = async (u) => {
    setLogsDialog({ open: true, user: u, sessions: [], activities: [] });
    setLoadingLogs(true);
    try {
      const token = localStorage.getItem('token');
      const [sessionsRes, activitiesRes] = await Promise.all([
        axios.get(`${API}/users/${u.id}/sessions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users/${u.id}/activity`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setLogsDialog(prev => ({
        ...prev,
        sessions: sessionsRes.data?.sessions || sessionsRes.data || [],
        activities: activitiesRes.data?.activities || activitiesRes.data || []
      }));
    } catch (error) {
      toast.error('Failed to load user logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  const openDeleteUserDialog = (userId, userName) => {
    setUserToDelete(userId);
    setDeleteUserDialog(true);
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center" style={{ fontFamily: 'Space Grotesk' }}>
              <Users className="w-5 h-5 mr-2" />
              User Management
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">Create and manage user accounts for your team</p>
          </div>
          <Dialog open={userDialog} onOpenChange={setUserDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddUser} className="space-y-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={userForm.name}
                    onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                    placeholder="e.g., John Doe"
                    required
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    placeholder="e.g., john@hoppers.com"
                    required
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    placeholder="Min 6 characters"
                    required
                    data-testid="user-password-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={userForm.phone}
                      onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                      placeholder="Optional"
                      data-testid="user-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Outlet</Label>
                    <Input
                      value={userForm.outlet}
                      onChange={(e) => setUserForm({ ...userForm, outlet: e.target.value })}
                      placeholder="Optional"
                      data-testid="user-outlet-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Role *</Label>
                  <select
                    className="w-full px-3 py-2 border rounded-md"
                    value={userForm.role}
                    onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    required
                    data-testid="user-role-select"
                  >
                    <option value="cashier">Cashier</option>
                    <option value="team_member">Team Member</option>
                    <option value="storekeeper">Storekeeper</option>
                    <option value="bartender">Bartender</option>
                    <option value="kitchen">Kitchen</option>
                    <option value="cafe">Cafe</option>
                    <option value="accountant">Accountant</option>
                    <option value="general_manager">General Manager</option>
                    <option value="partner">Partner</option>
                    <option value="admin">Admin/Owner</option>
                  </select>
                </div>
                <Button type="submit" className="w-full" data-testid="submit-user-btn" disabled={addingUser}>
                  {addingUser ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : 'Create User'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Circle 
                        className={`w-3 h-3 ${u.is_online ? 'fill-green-500 text-green-500' : 'fill-gray-300 text-gray-300'}`} 
                      />
                      <span className={`text-xs ${u.is_online ? 'text-green-600' : 'text-gray-400'}`}>
                        {u.is_online ? 'Online' : 'Offline'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{getRoleBadge(u.role)}</TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {u.last_login ? formatDateTime(u.last_login) : 'Never'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setNewPassword('');
                          setPasswordDialog({ open: true, user: u });
                        }}
                        title="Change Password"
                        data-testid={`password-user-${u.id}`}
                      >
                        <Key className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewLogs(u)}
                        title="View Logs"
                        data-testid={`logs-user-${u.id}`}
                      >
                        <History className="w-4 h-4 text-purple-500" />
                      </Button>
                      {u.id !== currentUserId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteUserDialog(u.id, u.name)}
                          title="Delete User"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {users.length === 0 && <p className="text-center text-gray-500 py-8">No users found</p>}
        </CardContent>
      </Card>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialog.open} onOpenChange={(open) => setPasswordDialog({ open, user: open ? passwordDialog.user : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password for {passwordDialog.user?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 6 characters"
                data-testid="new-password-input"
              />
            </div>
            <Button onClick={handleChangePassword} className="w-full" disabled={changingPassword}>
              {changingPassword ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Changing...</> : 'Change Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Logs Dialog */}
      <Dialog open={logsDialog.open} onOpenChange={(open) => setLogsDialog({ ...logsDialog, open })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Activity Logs: {logsDialog.user?.name}</DialogTitle>
          </DialogHeader>
          {loadingLogs ? (
            <div className="text-center py-8">Loading logs...</div>
          ) : (
            <Tabs defaultValue="sessions">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="sessions">Login Sessions</TabsTrigger>
                <TabsTrigger value="activity">Activity Log</TabsTrigger>
              </TabsList>

              <TabsContent value="sessions" className="mt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Login Time</TableHead>
                      <TableHead>Logout Time</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Logout Type</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logsDialog.sessions.slice(0, 20).map((session, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-sm">{formatDateTime(session.login_at)}</TableCell>
                        <TableCell className="text-sm">{session.logout_at ? formatDateTime(session.logout_at) : '-'}</TableCell>
                        <TableCell className="text-sm">{session.duration_minutes ? `${session.duration_minutes} mins` : '-'}</TableCell>
                        <TableCell>
                          {session.is_active ? (
                            <span className="text-gray-400">-</span>
                          ) : session.logout_reason === 'inactivity_timeout' ? (
                            <Badge className="bg-amber-100 text-amber-800 text-xs">Auto (Inactivity)</Badge>
                          ) : session.logout_reason === 'manual' ? (
                            <Badge className="bg-blue-100 text-blue-800 text-xs">Manual</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Unknown</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {session.is_active ? (
                            <Badge className="bg-green-100 text-green-800 text-xs">Active</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Ended</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {logsDialog.sessions.length === 0 && <p className="text-center py-4 text-gray-500">No login sessions</p>}
              </TabsContent>

              <TabsContent value="activity" className="mt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logsDialog.activities.slice(0, 50).map((activity, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-sm">{formatDateTime(activity.timestamp || activity.created_at)}</TableCell>
                        <TableCell>
                          <Badge 
                            variant={activity.action === 'login' ? 'default' : 'outline'}
                            className={
                              activity.action === 'auto_logout' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                              activity.action === 'logout' ? 'bg-gray-100 text-gray-800' :
                              activity.action === 'login' ? 'bg-green-100 text-green-800' :
                              ''
                            }
                          >
                            {activity.action === 'auto_logout' ? 'Auto Logout' : activity.action}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm max-w-xs truncate">{activity.details || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {logsDialog.activities.length === 0 && <p className="text-center py-4 text-gray-500">No activity logs</p>}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={deleteUserDialog} onOpenChange={setDeleteUserDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this user account. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700" disabled={deletingUser}>
              {deletingUser ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default UserManagement;
